/**
 * Digital Twin Viewer
 *
 * Unified viewer component that automatically detects file type
 * and renders the appropriate viewer (BIM, GIS, PDF, Image)
 */

'use client';

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { type DesignFile, type ViewerProps, getFileCategory, type FileCategory } from './types';
import BIMViewer, { type BIMViewerHandle, type SceneObjectInfo } from './BIMViewer';
import GISViewer, { type GISViewerHandle, type FileLayerInfo, type GISFeatureInfo, type GISFeatureType } from './GISViewer';
import FilePreview from './FilePreview';
import { type ViewerSettings } from './useViewerSettings';

// Layer info for tracking GIS sub-layers
interface FileLayerState {
  fileId: string;
  fileName: string;
  layers: Array<{
    id: string;
    name: string;
    featureCount: number;
    visible: boolean;
  }>;
}

interface DigitalTwinViewerProps extends ViewerProps {
  className?: string;
  title?: string;
  // UUID for persistence
  uuid?: string;
  // Persisted settings from IndexedDB
  persistedSettings?: ViewerSettings | null;
  // Callbacks for persisting settings changes
  onSettingsChange?: (settings: Partial<ViewerSettings>) => void;
  onGisLayerVisibilityChange?: (layerId: string, visible: boolean) => void;
  onGisFeatureVisibilityChange?: (featureId: string, visible: boolean) => void;
  onGisFeatureVisibilityBatchChange?: (updates: Record<string, boolean>) => void;
  onFeatureTypeExpandedChange?: (typeName: string, expanded: boolean) => void;
}

export default function DigitalTwinViewer({
  files,
  selectedFileId,
  onFileSelect,
  onElementSelect,
  height = '600px',
  showToolbar = true,
  showFileList = true,
  mode = 'view',
  className = '',
  title = 'Digital Twin Viewer',
  uuid,
  persistedSettings,
  onSettingsChange,
  onGisLayerVisibilityChange,
  onGisFeatureVisibilityChange,
  onGisFeatureVisibilityBatchChange,
  onFeatureTypeExpandedChange
}: DigitalTwinViewerProps) {
  const [activeFile, setActiveFile] = useState<DesignFile | null>(null);
  const [viewMode, setViewMode] = useState<'3d' | '2d' | 'split'>('3d');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Track if settings have been applied
  const settingsAppliedRef = useRef(false);

  // GIS layers state
  const gisViewerRef = useRef<GISViewerHandle>(null);
  const [fileLayers, setFileLayers] = useState<Map<string, FileLayerState>>(new Map());
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const reportedLayersRef = useRef<Set<string>>(new Set());

  // BIM objects state
  const bimViewerRef = useRef<BIMViewerHandle>(null);
  const [bimObjects, setBimObjects] = useState<SceneObjectInfo[]>([]);

  // GIS features state
  const [gisFeatures, setGisFeatures] = useState<GISFeatureInfo[]>([]);
  const [featureSearch, setFeatureSearch] = useState('');

  // Initialize state from persisted settings
  useEffect(() => {
    if (persistedSettings && !settingsAppliedRef.current) {
      settingsAppliedRef.current = true;

      // Restore view mode
      if (persistedSettings.viewMode) {
        setViewMode(persistedSettings.viewMode === 'full' ? '3d' : 'split');
      }

      // Restore sidebar state
      if (persistedSettings.sidebarCollapsed !== undefined) {
        setSidebarCollapsed(persistedSettings.sidebarCollapsed);
      }

      // Restore search
      if (persistedSettings.featureSearch) {
        setFeatureSearch(persistedSettings.featureSearch);
      }

      // Restore active file
      if (persistedSettings.activeFileId) {
        const file = files.find(f => f.id === persistedSettings.activeFileId);
        if (file) setActiveFile(file);
      }

      console.log('Applied persisted settings for UUID:', uuid);
    }
  }, [persistedSettings, files, uuid]);

  // Persist view mode changes
  const handleViewModeChange = useCallback((newMode: '3d' | '2d' | 'split') => {
    setViewMode(newMode);
    onSettingsChange?.({ viewMode: newMode === '3d' ? 'full' : 'split' });
  }, [onSettingsChange]);

  // Persist sidebar changes
  const handleSidebarToggle = useCallback(() => {
    setSidebarCollapsed(prev => {
      const newState = !prev;
      onSettingsChange?.({ sidebarCollapsed: newState });
      return newState;
    });
  }, [onSettingsChange]);

  // Persist active file changes
  const handleFileSelect = useCallback((file: DesignFile) => {
    setActiveFile(file);
    onFileSelect?.(file);
    onSettingsChange?.({ activeFileId: file.id, activeCategory: getFileCategory(file.name) });
  }, [onFileSelect, onSettingsChange]);

  // Helper to get file category - uses file.type if set, otherwise detects from filename
  const getFileCategoryForFile = useCallback((file: DesignFile): FileCategory => {
    // If type is explicitly set and valid, use it
    if (file.type && ['BIM', 'CAD', 'GIS', 'PDF', 'IMAGE', 'OTHER'].includes(file.type.toUpperCase())) {
      return file.type.toUpperCase() as FileCategory;
    }
    // Otherwise detect from filename
    return getFileCategory(file.name);
  }, []);

  // Group files by category
  const filesByCategory = useMemo(() => {
    const grouped: Record<FileCategory, DesignFile[]> = {
      BIM: [],
      CAD: [],
      GIS: [],
      PDF: [],
      IMAGE: [],
      OTHER: []
    };

    files.forEach(file => {
      const category = getFileCategoryForFile(file);
      grouped[category].push(file);
    });

    return grouped;
  }, [files, getFileCategoryForFile]);

  // Set active file
  useEffect(() => {
    if (selectedFileId) {
      const file = files.find(f => f.id === selectedFileId);
      if (file) setActiveFile(file);
    } else if (files.length > 0 && !activeFile) {
      setActiveFile(files[0]);
    }
  }, [selectedFileId, files]);

  // Handle GIS layers loaded - apply persisted visibility
  const handleLayersLoaded = useCallback((layerInfo: FileLayerInfo) => {
    // Apply persisted layer visibility if available
    const layersWithPersistedState = layerInfo.layers.map(layer => {
      const persistedVisible = persistedSettings?.gisLayerVisibility?.[layer.id];
      if (persistedVisible !== undefined) {
        // If persisted state differs from current, toggle it
        if (persistedVisible !== layer.visible) {
          gisViewerRef.current?.toggleLayer(layer.id);
        }
        return { ...layer, visible: persistedVisible };
      }
      return layer;
    });

    setFileLayers(prev => {
      const newMap = new Map(prev);
      newMap.set(layerInfo.fileId, {
        fileId: layerInfo.fileId,
        fileName: layerInfo.fileName,
        layers: layersWithPersistedState
      });
      return newMap;
    });
    // Auto-expand the file to show layers
    setExpandedFiles(prev => new Set(prev).add(layerInfo.fileId));

    if (persistedSettings?.gisLayerVisibility) {
      console.log('Applied persisted layer visibility');
    }
  }, [persistedSettings?.gisLayerVisibility]);

  // Toggle layer visibility
  const handleToggleLayer = useCallback((layerId: string) => {
    gisViewerRef.current?.toggleLayer(layerId);
    // Update local state
    setFileLayers(prev => {
      const newMap = new Map(prev);
      for (const [fileId, fileState] of newMap) {
        const layerIndex = fileState.layers.findIndex(l => l.id === layerId);
        if (layerIndex !== -1) {
          const updatedLayers = [...fileState.layers];
          const newVisible = !updatedLayers[layerIndex].visible;
          updatedLayers[layerIndex] = {
            ...updatedLayers[layerIndex],
            visible: newVisible
          };
          newMap.set(fileId, { ...fileState, layers: updatedLayers });
          // Persist to IndexedDB
          onGisLayerVisibilityChange?.(layerId, newVisible);
          break;
        }
      }
      return newMap;
    });
  }, [onGisLayerVisibilityChange]);

  // Zoom to layer
  const handleZoomToLayer = useCallback((layerId: string) => {
    gisViewerRef.current?.zoomToLayer(layerId);
  }, []);

  // Handle BIM objects loaded
  const handleBimObjectsLoaded = useCallback((objects: SceneObjectInfo[]) => {
    console.log('DigitalTwinViewer received BIM objects:', objects.length, objects);
    setBimObjects(objects);
  }, []);

  // Toggle BIM object visibility
  const handleToggleBimObject = useCallback((objectId: string) => {
    bimViewerRef.current?.toggleObjectVisibility(objectId);
    // Update local state
    setBimObjects(prev => prev.map(obj =>
      obj.id === objectId ? { ...obj, visible: !obj.visible } : obj
    ));
  }, []);

  // Zoom to BIM object
  const handleZoomToBimObject = useCallback((objectId: string) => {
    bimViewerRef.current?.zoomToObject(objectId);
  }, []);

  // Handle GIS features loaded - apply persisted visibility
  const handleGisFeaturesLoaded = useCallback((features: GISFeatureInfo[]) => {
    console.log('GIS features loaded:', features.length);

    // Apply persisted visibility states if available
    if (persistedSettings?.gisFeatureVisibility) {
      const updatedFeatures = features.map(f => {
        const persistedVisible = persistedSettings.gisFeatureVisibility?.[f.id];
        if (persistedVisible !== undefined) {
          // If persisted state differs from current, toggle it
          if (persistedVisible !== f.visible) {
            gisViewerRef.current?.toggleFeature(f.id);
          }
          return { ...f, visible: persistedVisible };
        }
        return f;
      });
      setGisFeatures(updatedFeatures);
      console.log('Applied persisted feature visibility for', Object.keys(persistedSettings.gisFeatureVisibility).length, 'features');
    } else {
      setGisFeatures(features);
    }
  }, [persistedSettings?.gisFeatureVisibility]);

  // Toggle GIS feature visibility
  const handleToggleGisFeature = useCallback((featureId: string) => {
    gisViewerRef.current?.toggleFeature(featureId);
    setGisFeatures(prev => {
      const feature = prev.find(f => f.id === featureId);
      if (feature) {
        const newVisible = !feature.visible;
        // Persist to IndexedDB
        onGisFeatureVisibilityChange?.(featureId, newVisible);
      }
      return prev.map(f =>
        f.id === featureId ? { ...f, visible: !f.visible } : f
      );
    });
  }, [onGisFeatureVisibilityChange]);

  // Zoom to GIS feature
  const handleZoomToGisFeature = useCallback((featureId: string) => {
    gisViewerRef.current?.zoomToFeature(featureId);
  }, []);

  // Toggle file expansion
  const toggleFileExpansion = useCallback((fileId: string) => {
    setExpandedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  }, []);

  // Determine which viewer to use
  const activeCategory = activeFile ? getFileCategoryForFile(activeFile) : null;

  // Get files for current viewer
  // Include .dwg files - BIMViewer will convert them to OBJ format
  const bimFiles = [...filesByCategory.BIM, ...filesByCategory.CAD].filter(f =>
    ['.ifc', '.gltf', '.glb', '.obj', '.dxf', '.dwg'].some(ext => f.name.toLowerCase().endsWith(ext))
  );
  const gisFiles = filesByCategory.GIS;

  const getCategoryIcon = (category: FileCategory) => {
    switch (category) {
      case 'BIM': return 'ri-building-3-line';
      case 'CAD': return 'ri-pencil-ruler-2-line';
      case 'GIS': return 'ri-map-2-line';
      case 'PDF': return 'ri-file-pdf-line';
      case 'IMAGE': return 'ri-image-line';
      default: return 'ri-file-line';
    }
  };

  const getCategoryColor = (category: FileCategory) => {
    switch (category) {
      case 'BIM': return 'blue';
      case 'CAD': return 'purple';
      case 'GIS': return 'teal';
      case 'PDF': return 'red';
      case 'IMAGE': return 'green';
      default: return 'gray';
    }
  };

  return (
    <div
      className={`bg-gray-900 rounded-lg overflow-hidden flex flex-col ${className} ${isFullscreen ? 'fixed inset-0 z-[9999] rounded-none' : ''}`}
      style={{ height: isFullscreen ? '100vh' : height }}
    >
      {/* Header */}
      {showToolbar && (
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <i className="ri-3d-cube-sphere-line text-white"></i>
            </div>
            <div>
              <h3 className="text-white font-medium text-sm">{title}</h3>
              {activeFile && (
                <p className="text-gray-400 text-xs truncate max-w-[200px]">{activeFile.name}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="bg-gray-700 rounded-lg p-1 flex">
              <button
                onClick={() => setViewMode('3d')}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  viewMode === '3d' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
                title="3D View"
              >
                <i className="ri-3d-cube-sphere-line mr-1"></i>3D
              </button>
              <button
                onClick={() => setViewMode('2d')}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  viewMode === '2d' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
                title="2D Map View"
              >
                <i className="ri-map-2-line mr-1"></i>2D
              </button>
              <button
                onClick={() => setViewMode('split')}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  viewMode === 'split' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
                title="Split View"
              >
                <i className="ri-layout-column-line mr-1"></i>Split
              </button>
            </div>

            {/* Tools */}
            <div className="h-6 w-px bg-gray-600"></div>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg" title="Measure">
              <i className="ri-ruler-line"></i>
            </button>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg" title="Layers">
              <i className="ri-stack-line"></i>
            </button>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg" title="Settings">
              <i className="ri-settings-3-line"></i>
            </button>

            <div className="h-6 w-px bg-gray-600"></div>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <i className={isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}></i>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* File Sidebar */}
        {showFileList && (
          <div className={`bg-gray-800 border-r border-gray-700 transition-all flex flex-col ${sidebarCollapsed ? 'w-12' : 'w-64'}`}>
            <div className="p-2 border-b border-gray-700 flex items-center justify-between">
              {!sidebarCollapsed && <span className="text-gray-400 text-xs font-medium">FILES</span>}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1 text-gray-400 hover:text-white rounded"
              >
                <i className={sidebarCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'}></i>
              </button>
            </div>

            {!sidebarCollapsed && (
              <div className="flex-1 overflow-y-auto">
                {Object.entries(filesByCategory).map(([category, categoryFiles]) => {
                  if (categoryFiles.length === 0) return null;
                  const color = getCategoryColor(category as FileCategory);
                  const icon = getCategoryIcon(category as FileCategory);

                  return (
                    <div key={category} className="mb-2">
                      <div className={`px-3 py-1.5 text-xs font-medium flex items-center gap-2 text-${color}-400`}>
                        <i className={icon}></i>
                        {category} ({categoryFiles.length})
                      </div>
                      {categoryFiles.map(file => {
                        const fileLayerState = fileLayers.get(file.id);
                        const hasLayers = fileLayerState && fileLayerState.layers.length > 0;
                        const isExpanded = expandedFiles.has(file.id);

                        return (
                          <div key={file.id}>
                            <div
                              className={`w-full px-3 py-2 text-left flex items-center gap-2 transition-colors cursor-pointer ${
                                activeFile?.id === file.id
                                  ? `bg-${color}-600/20 border-l-2 border-${color}-500`
                                  : 'hover:bg-gray-700/50'
                              }`}
                            >
                              {/* Expand/Collapse button for GIS files with layers */}
                              {category === 'GIS' && hasLayers ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleFileExpansion(file.id); }}
                                  className="text-gray-400 hover:text-white"
                                >
                                  <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line`}></i>
                                </button>
                              ) : (
                                <span className="w-4"></span>
                              )}
                              <button
                                onClick={() => handleFileSelect(file)}
                                className="flex items-center gap-2 flex-1 min-w-0"
                              >
                                <i className={`${icon} text-${color}-500`}></i>
                                <span className="text-gray-300 text-xs truncate flex-1">{file.name}</span>
                              </button>
                              {hasLayers && (
                                <span className="text-gray-500 text-[10px]">
                                  {fileLayerState!.layers.length}
                                </span>
                              )}
                            </div>

                            {/* Sub-layers for GIS files */}
                            {category === 'GIS' && hasLayers && isExpanded && (
                              <div className="ml-4 border-l border-gray-700">
                                {fileLayerState!.layers.map(layer => (
                                  <div
                                    key={layer.id}
                                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-700/30"
                                  >
                                    <button
                                      onClick={() => handleToggleLayer(layer.id)}
                                      className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                        layer.visible
                                          ? 'bg-teal-500 border-teal-500 text-white'
                                          : 'border-gray-500 text-transparent hover:border-gray-400'
                                      }`}
                                      title={layer.visible ? 'Hide layer' : 'Show layer'}
                                    >
                                      <i className="ri-check-line text-[10px]"></i>
                                    </button>
                                    <span className="text-gray-400 text-xs truncate flex-1" title={layer.name}>
                                      {layer.name}
                                    </span>
                                    <span className="text-gray-600 text-[10px]">{layer.featureCount}</span>
                                    <button
                                      onClick={() => handleZoomToLayer(layer.id)}
                                      className="text-gray-500 hover:text-teal-400 p-0.5"
                                      title="Zoom to layer"
                                    >
                                      <i className="ri-focus-3-line text-xs"></i>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {files.length === 0 && (
                  <div className="px-3 py-8 text-center text-gray-500 text-sm">
                    <i className="ri-file-upload-line text-3xl mb-2 block opacity-50"></i>
                    No files uploaded
                  </div>
                )}
              </div>
            )}

            {/* Objects/Features Panel - Fixed at bottom of sidebar */}
            {!sidebarCollapsed && (
              <div className="border-t border-gray-700 bg-gray-800 flex flex-col max-h-[40%]">
                {/* Show BIM Objects when CAD/BIM file is active */}
                {(activeCategory === 'BIM' || activeCategory === 'CAD') && (
                  <>
                    <div className="p-2 flex items-center justify-between flex-shrink-0">
                      <span className="text-gray-400 text-xs font-medium">OBJECTS</span>
                      <span className="text-gray-500 text-[10px]">{bimObjects.length}</span>
                    </div>
                    {bimObjects.length > 0 ? (
                      <div className="flex-1 overflow-y-auto">
                        {bimObjects.map(obj => (
                          <div
                            key={obj.id}
                            className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-700/30 group"
                          >
                            <button
                              onClick={() => handleToggleBimObject(obj.id)}
                              className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                                obj.visible
                                  ? 'bg-indigo-500 border-indigo-500 text-white'
                                  : 'border-gray-500 text-transparent hover:border-gray-400'
                              }`}
                            >
                              <i className="ri-check-line text-[10px]"></i>
                            </button>
                            <i className={`text-sm flex-shrink-0 ${
                              obj.type === 'line' ? 'ri-pencil-ruler-line text-yellow-400' :
                              obj.type === 'mesh' ? 'ri-box-3-line text-blue-400' :
                              obj.type === 'helper' ? 'ri-grid-line text-gray-500' :
                              'ri-shape-line text-gray-400'
                            }`}></i>
                            <span className={`text-xs truncate flex-1 ${obj.visible ? 'text-gray-300' : 'text-gray-500'}`}>
                              {obj.name}
                            </span>
                            <span className="text-gray-600 text-[10px]">{obj.vertexCount.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-3 py-4 text-center text-gray-500 text-xs">
                        No objects loaded
                      </div>
                    )}
                  </>
                )}

                {/* Show GIS Features when GIS file is active - Grouped by Type */}
                {activeCategory === 'GIS' && (
                  <>
                    <div className="p-2 flex items-center justify-between flex-shrink-0">
                      <span className="text-gray-400 text-xs font-medium">FEATURES</span>
                      <span className="text-gray-500 text-[10px]">{gisFeatures.length}</span>
                    </div>
                    {/* Search */}
                    {gisFeatures.length > 0 && (
                      <div className="px-2 pb-2 flex-shrink-0">
                        <div className="relative">
                          <input
                            type="text"
                            value={featureSearch}
                            onChange={(e) => setFeatureSearch(e.target.value)}
                            placeholder="Search features..."
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200 placeholder-gray-500 focus:outline-none focus:border-teal-500"
                          />
                          {featureSearch && (
                            <button
                              onClick={() => setFeatureSearch('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {gisFeatures.length > 0 ? (
                      <GISFeaturesGrouped
                        features={gisFeatures}
                        featureSearch={featureSearch}
                        onToggleFeature={handleToggleGisFeature}
                        onZoomToFeature={handleZoomToGisFeature}
                        onToggleType={(type, visible, featureIds) => {
                          gisViewerRef.current?.toggleFeaturesByType(type, visible);
                          // Batch update features visibility in local state
                          setGisFeatures(prev => prev.map(f =>
                            featureIds.includes(f.id) ? { ...f, visible } : f
                          ));
                          // Persist batch update
                          if (onGisFeatureVisibilityBatchChange) {
                            const updates: Record<string, boolean> = {};
                            featureIds.forEach(id => { updates[id] = visible; });
                            onGisFeatureVisibilityBatchChange(updates);
                          }
                        }}
                        persistedExpandedTypes={persistedSettings?.featureTypeExpanded}
                        onExpandedTypeChange={onFeatureTypeExpandedChange}
                      />
                    ) : (
                      <div className="px-3 py-4 text-center text-gray-500 text-xs">
                        No features loaded
                      </div>
                    )}
                  </>
                )}

                {/* Show placeholder when no active category */}
                {activeCategory !== 'BIM' && activeCategory !== 'CAD' && activeCategory !== 'GIS' && (
                  <div className="p-2">
                    <span className="text-gray-400 text-xs font-medium">OBJECTS</span>
                    <div className="px-3 py-4 text-center text-gray-500 text-xs">
                      Select a file to view objects
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Viewer Area */}
        <div className="flex-1 flex">
          {viewMode === 'split' ? (
            <>
              {/* 3D View */}
              <div className="flex-1 border-r border-gray-700">
                {bimFiles.length > 0 ? (
                  <BIMViewer
                    ref={bimViewerRef}
                    files={bimFiles}
                    selectedFileId={activeCategory === 'BIM' || activeCategory === 'CAD' ? activeFile?.id : undefined}
                    onElementSelect={onElementSelect}
                    onObjectsLoaded={handleBimObjectsLoaded}
                    height="100%"
                  />
                ) : (
                  <EmptyViewer message="No 3D files available" icon="ri-3d-cube-sphere-line" />
                )}
              </div>
              {/* 2D View */}
              <div className="flex-1">
                {gisFiles.length > 0 ? (
                  <GISViewer
                    ref={gisViewerRef}
                    files={gisFiles}
                    selectedFileId={activeCategory === 'GIS' ? activeFile?.id : undefined}
                    onElementSelect={onElementSelect}
                    onLayersLoaded={handleLayersLoaded}
                    onFeaturesLoaded={handleGisFeaturesLoaded}
                    height="100%"
                  />
                ) : (
                  <EmptyViewer message="No GIS files available" icon="ri-map-2-line" />
                )}
              </div>
            </>
          ) : viewMode === '3d' ? (
            // 3D View Only
            activeCategory === 'BIM' || activeCategory === 'CAD' ? (
              <BIMViewer
                ref={bimViewerRef}
                files={bimFiles}
                selectedFileId={activeFile?.id}
                onElementSelect={onElementSelect}
                onObjectsLoaded={handleBimObjectsLoaded}
                height="100%"
              />
            ) : activeCategory === 'GIS' ? (
              <GISViewer
                ref={gisViewerRef}
                files={gisFiles}
                selectedFileId={activeFile?.id}
                onElementSelect={onElementSelect}
                onLayersLoaded={handleLayersLoaded}
                onFeaturesLoaded={handleGisFeaturesLoaded}
                height="100%"
                mode="3d"
              />
            ) : activeFile ? (
              <FilePreview file={activeFile} />
            ) : (
              <EmptyViewer message="Select a file to view" icon="ri-eye-line" />
            )
          ) : (
            // 2D View Only
            activeCategory === 'GIS' ? (
              <GISViewer
                ref={gisViewerRef}
                files={gisFiles}
                selectedFileId={activeFile?.id}
                onElementSelect={onElementSelect}
                onLayersLoaded={handleLayersLoaded}
                onFeaturesLoaded={handleGisFeaturesLoaded}
                height="100%"
                mode="2d"
              />
            ) : activeCategory === 'BIM' || activeCategory === 'CAD' ? (
              <BIMViewer
                ref={bimViewerRef}
                files={bimFiles}
                selectedFileId={activeFile?.id}
                onElementSelect={onElementSelect}
                onObjectsLoaded={handleBimObjectsLoaded}
                height="100%"
                mode="2d"
              />
            ) : activeFile ? (
              <FilePreview file={activeFile} />
            ) : (
              <EmptyViewer message="Select a file to view" icon="ri-eye-line" />
            )
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-1.5 flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-4">
          <span>{files.length} file(s)</span>
          {activeFile && (
            <span className="flex items-center gap-1">
              <i className={getCategoryIcon(activeCategory as FileCategory)}></i>
              {activeFile.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span>Mode: {viewMode.toUpperCase()}</span>
          {activeFile?.size && (
            <span>{formatFileSize(activeFile.size)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Empty viewer placeholder
function EmptyViewer({ message, icon }: { message: string; icon: string }) {
  return (
    <div className="flex-1 flex items-center justify-center bg-gray-900">
      <div className="text-center text-gray-500">
        <i className={`${icon} text-5xl mb-3 block opacity-30`}></i>
        <p>{message}</p>
      </div>
    </div>
  );
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// GIS Features grouped by type component
interface GISFeaturesGroupedProps {
  features: GISFeatureInfo[];
  featureSearch: string;
  onToggleFeature: (featureId: string) => void;
  onZoomToFeature: (featureId: string) => void;
  onToggleType: (type: GISFeatureType, visible: boolean, featureIds: string[]) => void;
  // Persistence props
  persistedExpandedTypes?: Record<string, boolean>;
  onExpandedTypeChange?: (typeName: string, expanded: boolean) => void;
}

function GISFeaturesGrouped({
  features,
  featureSearch,
  onToggleFeature,
  onZoomToFeature,
  onToggleType,
  persistedExpandedTypes,
  onExpandedTypeChange
}: GISFeaturesGroupedProps) {
  // Initialize expanded types from persisted settings or default to all expanded
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(() => {
    if (persistedExpandedTypes) {
      const expanded = new Set<string>();
      Object.entries(persistedExpandedTypes).forEach(([type, isExpanded]) => {
        if (isExpanded) expanded.add(type);
      });
      return expanded.size > 0 ? expanded : new Set(['Points', 'Lines', 'Polygons']);
    }
    return new Set(['Points', 'Lines', 'Polygons']);
  });

  // Filter features based on search
  const filteredFeatures = features.filter(f =>
    !featureSearch ||
    f.name.toLowerCase().includes(featureSearch.toLowerCase()) ||
    f.layerName.toLowerCase().includes(featureSearch.toLowerCase()) ||
    f.type.toLowerCase().includes(featureSearch.toLowerCase())
  );

  // Group features by type
  const groupedFeatures = useMemo(() => {
    const groups: {
      Points: GISFeatureInfo[];
      Lines: GISFeatureInfo[];
      Polygons: GISFeatureInfo[];
    } = {
      Points: [],
      Lines: [],
      Polygons: []
    };

    filteredFeatures.forEach(f => {
      if (f.type === 'Point') {
        groups.Points.push(f);
      } else if (f.type === 'LineString' || f.type === 'MultiLineString') {
        groups.Lines.push(f);
      } else if (f.type === 'Polygon' || f.type === 'MultiPolygon') {
        groups.Polygons.push(f);
      }
    });

    return groups;
  }, [filteredFeatures]);

  // Calculate visibility stats for each group
  const groupStats = useMemo(() => {
    return {
      Points: {
        total: groupedFeatures.Points.length,
        visible: groupedFeatures.Points.filter(f => f.visible).length
      },
      Lines: {
        total: groupedFeatures.Lines.length,
        visible: groupedFeatures.Lines.filter(f => f.visible).length
      },
      Polygons: {
        total: groupedFeatures.Polygons.length,
        visible: groupedFeatures.Polygons.filter(f => f.visible).length
      }
    };
  }, [groupedFeatures]);

  const toggleTypeExpansion = (type: string) => {
    setExpandedTypes(prev => {
      const newSet = new Set(prev);
      const newExpanded = !newSet.has(type);
      if (newExpanded) {
        newSet.add(type);
      } else {
        newSet.delete(type);
      }
      // Persist the change
      onExpandedTypeChange?.(type, newExpanded);
      return newSet;
    });
  };

  const handleToggleAllInGroup = (groupName: 'Points' | 'Lines' | 'Polygons') => {
    const stats = groupStats[groupName];
    const shouldShow = stats.visible < stats.total; // Show all if not all visible

    // Map group name to GISFeatureType
    const typeMap: Record<string, GISFeatureType> = {
      Points: 'Point',
      Lines: 'LineString',
      Polygons: 'Polygon'
    };

    // Get feature IDs for this group
    const featureIds = groupedFeatures[groupName].map(f => f.id);

    onToggleType(typeMap[groupName], shouldShow, featureIds);
  };

  const groupConfig = [
    {
      name: 'Points' as const,
      icon: 'ri-map-pin-line',
      iconClass: 'text-red-400',
      activeClass: 'bg-red-500 border-red-500',
      partialClass: 'bg-red-500/50 border-red-500',
      label: 'Points'
    },
    {
      name: 'Lines' as const,
      icon: 'ri-route-line',
      iconClass: 'text-blue-400',
      activeClass: 'bg-blue-500 border-blue-500',
      partialClass: 'bg-blue-500/50 border-blue-500',
      label: 'Lines'
    },
    {
      name: 'Polygons' as const,
      icon: 'ri-shape-line',
      iconClass: 'text-green-400',
      activeClass: 'bg-green-500 border-green-500',
      partialClass: 'bg-green-500/50 border-green-500',
      label: 'Polygons'
    }
  ];

  return (
    <div className="flex-1 overflow-y-auto">
      {groupConfig.map(group => {
        const groupFeatures = groupedFeatures[group.name];
        const stats = groupStats[group.name];
        const isExpanded = expandedTypes.has(group.name);
        const allVisible = stats.visible === stats.total && stats.total > 0;
        const noneVisible = stats.visible === 0;

        if (groupFeatures.length === 0) return null;

        return (
          <div key={group.name} className="mb-1">
            {/* Group Header */}
            <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-800/50 hover:bg-gray-700/50">
              <button
                onClick={() => toggleTypeExpansion(group.name)}
                className="text-gray-400 hover:text-white p-0.5"
              >
                <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-sm`}></i>
              </button>
              <button
                onClick={() => handleToggleAllInGroup(group.name)}
                className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                  allVisible
                    ? `${group.activeClass} text-white`
                    : noneVisible
                    ? 'border-gray-500 text-transparent hover:border-gray-400'
                    : `${group.partialClass} text-white`
                }`}
                title={allVisible ? 'Hide all' : 'Show all'}
              >
                <i className={`text-[10px] ${allVisible ? 'ri-check-line' : noneVisible ? '' : 'ri-subtract-line'}`}></i>
              </button>
              <i className={`${group.icon} ${group.iconClass} text-sm`}></i>
              <span className="text-gray-300 text-xs font-medium flex-1">{group.label}</span>
              <span className="text-gray-500 text-[10px]">
                {stats.visible}/{stats.total}
              </span>
            </div>

            {/* Group Features */}
            {isExpanded && (
              <div className="ml-4 border-l border-gray-700">
                {groupFeatures.map(feature => (
                  <div
                    key={feature.id}
                    className="flex items-center gap-2 px-2 py-1 hover:bg-gray-700/30 group"
                  >
                    <button
                      onClick={() => onToggleFeature(feature.id)}
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                        feature.visible
                          ? `${group.activeClass} text-white`
                          : 'border-gray-500 text-transparent hover:border-gray-400'
                      }`}
                    >
                      <i className="ri-check-line text-[8px]"></i>
                    </button>
                    <div className="flex-1 min-w-0">
                      <span className={`text-[11px] truncate block ${feature.visible ? 'text-gray-300' : 'text-gray-500'}`}>
                        {feature.name}
                      </span>
                      <span className="text-[9px] text-gray-600 truncate block">
                        {feature.layerName}
                      </span>
                    </div>
                    <button
                      onClick={() => onZoomToFeature(feature.id)}
                      className="text-gray-500 hover:text-teal-400 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Zoom to feature"
                    >
                      <i className="ri-focus-3-line text-[10px]"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
